import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { fetchData } from '../../../helpers/fetchData'
import { payersOptionsFormat } from '../../../constants/BeneficiaryConstants'
import CustomButton from '../../../shared/CustomButton'
import CustomPagination from '../../../shared/CustomPagination'
import CustomTable from '../../../shared/CustomTable'
import DatePicker from '../../../shared/DatePicker'
import DropdownField from '../../../shared/DropdownField'
import MultiSelectField from '../../../shared/MultiSelectField'
import ReusableModal from '../../../shared/ReuseableModal'
import TextInput from '../../../shared/TextInput'
import {
  MdOutlineAdd,
  MdOutlineCalendarMonth,
  MdOutlineDateRange,
  MdOutlineMoveUp
} from 'react-icons/md'
import { EyeIcon } from '../../../utils/TableActionIcons'
import { MoveStepIcon, JobProgressIcon } from '../../../utils/ClaimsActionIcons'
import JobProgressModal from './JobProgressModal'
import RemarkModal from './RemarkModal'
import ConfirmationModal from '../../../shared/ConfirmationModal'

const GROUP_PREFIX = '__tpa__'

const isGroupToken = value =>
  typeof value === 'string' && value.startsWith(GROUP_PREFIX)


const getGroupEntries = groupedOptions => {
  const entries = []
    ; (groupedOptions || []).forEach(group => {
      const payerOptions = (group?.options || []).filter(option => !option?.isGroupOption)
      const payerIds = payerOptions.map(option => option.value).filter(Boolean)
      if (!payerIds.length) return
      entries.push({
        token: `${GROUP_PREFIX}${group.label}`,
        payerIds
      })
    })
  return entries
}

const toEffectiveValue = (payerIds, groupedOptions) => {
  const selected = new Set(payerIds || [])
  const effective = Array.from(selected)
  getGroupEntries(groupedOptions).forEach(entry => {
    if (entry.payerIds.every(id => selected.has(id))) {
      effective.push(entry.token)
    }
  })
  return effective
}

const fromEffectiveValue = (effectiveValues, groupedOptions) => {
  const picked = new Set(effectiveValues || [])
  const result = new Set()
  getGroupEntries(groupedOptions).forEach(entry => {
    if (picked.has(entry.token)) {
      entry.payerIds.forEach(id => result.add(id))
    } else {
      entry.payerIds.forEach(id => {
        if (picked.has(id)) result.add(id)
      })
    }
  })
    ; (effectiveValues || []).forEach(value => {
      if (!isGroupToken(value)) result.add(value)
    })
  return Array.from(result)
}

const tokenChipTemplate = (option, { t }) => {
  if (isGroupToken(option?.value)) {
    return <span className='hidden' />
  }
  return <span>{option?.label}</span>
}

const batchStepOrder = {
  audit: 1,
  auto_fix: 2,
  acknowledgment: 3,
  generate_claim: 4,
  generated_claim_audit: 5,
  generated_claim_auto_fix: 6,
  clearance: 7,
  submit_claim: 8
}

const normalizeText = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim()

const getBatchFinancials = row => {
  const generated = row?.generated_financials
  if (generated && Object.keys(generated).length) return generated

  const initialized = row?.initialization_financials
  if (initialized && Object.keys(initialized).length) {
    const patientShare = Number(initialized.patient_share || 0)
    const payerShare = Number(initialized.payer_share || 0)
    return {
      company_share: payerShare,
      patient_share: patientShare,
      discount: Number(initialized.discount || 0),
      vat:
        Number(initialized.patient_share_with_vat || 0) - patientShare +
        (Number(initialized.payer_share_with_vat || 0) - payerShare)
    }
  }

  const itemFinancials = row?.financials || {}
  return {
    company_share: Number(itemFinancials.company_share || 0),
    patient_share: Number(itemFinancials.patient_share || 0),
    discount: Number(itemFinancials.discount || 0),
    vat: Number(itemFinancials.total_vat || 0)
  }
}

const formatBatchAmount = value => Number(value || 0).toFixed(2)
function ClaimBatchListTab({ selectedBatchId, setSelectedBatchId, onBatchCreated }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [payers, setPayers] = useState([])
  const [batches, setBatches] = useState([])
  const [pagination, setPagination] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('claimBatchListPagination') || '{}')
      return {
        page: Number(stored?.page) || 1,
        per_page: Number(stored?.per_page) || 10,
        total_count: 0,
        total_pages: 0
      }
    } catch (error) {
      return { page: 1, per_page: 10, total_count: 0, total_pages: 0 }
    }
  })
  const initialFilters = useMemo(
    () => ({
      month: moment().format('YYYY-MM'),
      payer_ids: [],
      search: ''
    }),
    []
  )
  const initialCreateForm = useMemo(
    () => ({
      start_date: moment().startOf('month').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD')
    }),
    []
  )
  const [form, setForm] = useState(() => {
    const stored = localStorage.getItem('claimBatchList_filters')
    if (!stored) return initialFilters

    try {
      const persisted = JSON.parse(stored)
      const persistedMonth = persisted?.month || moment(persisted?.start_date).format('YYYY-MM')

      return {
        ...initialFilters,
        ...persisted,
        month: persistedMonth && persistedMonth !== 'Invalid date' ? persistedMonth : initialFilters.month,
        payer_ids: Array.isArray(persisted?.payer_ids) ? persisted.payer_ids : [],
        search: persisted?.search || ''
      }
    } catch (error) {
      console.warn('Failed to parse stored batch filters:', error)
      return initialFilters
    }
  })
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [createMode, setCreateMode] = useState('range')
  const [createForm, setCreateForm] = useState(initialCreateForm)
  const [moveBatch, setMoveBatch] = useState(null)
  const [movingStep, setMovingStep] = useState(false)
  const [moveForm, setMoveForm] = useState({ target_step: '', remarks: '' })
  const [jobBatch, setJobBatch] = useState(null)
  const moveStepOptions = useMemo(() => {
    const currentOrder = batchStepOrder[moveBatch?.current_step] || 0
    return [
      { value: 'audit', label: t('Audit') },
      { value: 'auto_fix', label: t('Auto Correct') },
      { value: 'acknowledgment', label: t('Acknowledge') },
      { value: 'generate_claim', label: t('Generate Claim') },
      { value: 'generated_claim_audit', label: t('Generated Claim Audit') },
      { value: 'generated_claim_auto_fix', label: t('Generated Claim Auto Correct') },
      { value: 'clearance', label: t('Clearance') },
      { value: 'submit_claim', label: t('Submit Claim') }
    ].filter(option => batchStepOrder[option.value] < currentOrder)
  }, [moveBatch, t])

  const groupedPayerOptions = useMemo(() => {
    const grouped = payersOptionsFormat(payers)

    return grouped.map(group => {
      const payerIds = (group?.options || [])
        .map(option => option.value)
        .filter(Boolean)

      return {
        ...group,
        options: [
          {
            label: `${t('Select All')} - ${group.label}`,
            value: `${GROUP_PREFIX}${group.label}`,
            isGroupOption: true,
            payerIds
          },
          ...(group?.options || [])
        ]
      }
    })
  }, [payers, t])

  const selectedPayerNames = useMemo(() => {
    const selectedSet = new Set(form.payer_ids)
    return payers
      .filter(item =>
        selectedSet.has(item?.payer_id || item?.his_payer_id || item?.id)
      )
      .map(item => item?.payer_name || item?.name || item?.company_name)
      .filter(Boolean)
  }, [form.payer_ids, payers])

  const fetchPayers = async () => {
    try {
      const response = await fetchData('POST', '/v3/payer/get', {})
      setPayers(response?.data || [])
    } catch (error) {
      console.error('Error fetching payers', error)
    }
  }

  const fetchBatches = async (page = 1, perPage = 10, filters = form) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage)
      })
      if (filters?.month) params.set('month', filters.month)
      if (filters?.search?.trim()) params.set('search', filters.search.trim())
      if (filters?.payer_ids?.length) params.set('payer_ids', filters.payer_ids.join(','))
      params.set('include_financials', 'true')

      const response = await fetchData('GET', `v3/claims/batches?${params.toString()}`, {})
      setBatches(response?.data || [])
      setPagination({
        page: response?.page || page,
        per_page: response?.per_page || perPage,
        total_count: response?.total_count || 0,
        total_pages: response?.total_pages || 0
      })
    } catch (error) {
      console.error('Error fetching batches', error)
      setBatches([])
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    localStorage.setItem('claimBatchList_filters', JSON.stringify(form))
  }, [form])

  useEffect(() => {
    fetchPayers()
    fetchBatches(pagination.page, pagination.per_page)
  }, [])

  useEffect(() => {
    localStorage.setItem(
      'claimBatchListPagination',
      JSON.stringify({ page: pagination.page, per_page: pagination.per_page })
    )
  }, [pagination.page, pagination.per_page])

  const handlePayerChange = event => {
    const selectedValues = event.value || []
    setForm(prev => ({
      ...prev,
      payer_ids: fromEffectiveValue(selectedValues, groupedPayerOptions)
    }))
  }

  const handleApplyFilters = () => {
    fetchBatches(1, pagination.per_page, form)
  }

  const handleResetFilters = () => {
    const resetFilters = initialFilters
    setForm(resetFilters)
    fetchBatches(1, pagination.per_page, resetFilters)
  }


  const handleMonthChange = event => {
    const monthValue = event.target.value
    if (!monthValue) return
    const selectedMonth = moment(monthValue, 'YYYY-MM')
    setCreateForm(prev => ({
      ...prev,
      start_date: selectedMonth.clone().startOf('month').format('YYYY-MM-DD'),
      end_date: selectedMonth.clone().endOf('month').format('YYYY-MM-DD')
    }))
  }
  const handleCreate = async () => {

    setCreating(true)
    try {
      const response = await fetchData('POST', 'v3/claims/batches', createForm)
      const batchId = response?.batch?.batch_id
      const createdCount = response?.batches?.length || (batchId ? 1 : 0)
      toast.success(createdCount > 1 ? t('{{count}} batches generated successfully', { count: createdCount }) : t('Batch generated successfully'))
      await fetchBatches(1, pagination.per_page, form)
      setIsCreateModalVisible(false)
      setShowGenerateConfirm(false)
      if (batchId) {
        setSelectedBatchId(batchId)
        onBatchCreated?.({
          batchId,
          currentStep: response?.batch?.current_step || 'audit',
          month: moment(createForm.start_date, 'YYYY-MM-DD').format('YYYY-MM')
        })
      }
    } catch (error) {
      console.error('Error creating batch', error)
      toast.error(error?.response?.data?.error || t('Failed to create batch'))
    } finally {
      setCreating(false)
    }
  }

  const openCurrentJobModal = row => {
    if (!row?.batch_id || !row?.current_job?.job_id) return
    setJobBatch(row)
  }

  const openMoveStepModal = row => {
    setMoveBatch(row)
    setMoveForm({ target_step: '', remarks: '' })
  }

  const handleMoveStep = async remark => {
    if (!moveBatch?.batch_id || !moveForm.target_step) {
      toast.error(t('Please select a step'))
      return
    }
    if (batchStepOrder[moveForm.target_step] >= (batchStepOrder[moveBatch.current_step] || 0)) {
      toast.error(t('A batch can only be moved back to an earlier step'))
      return
    }
    if (!remark?.trim()) {
      toast.error(t('Please enter a reason for the status change'))
      return
    }

    setMovingStep(true)
    try {
      await fetchData('POST', 'v3/claims/batches/' + moveBatch.batch_id + '/move-step', {
        target_step: moveForm.target_step,
        remarks: remark.trim()
      })
      toast.success(t('Batch step moved successfully'))
      setMoveBatch(null)
      await fetchBatches(pagination.page, pagination.per_page, form)
    } catch (error) {
      console.error('Error moving batch step', error)
      toast.error(error?.response?.data?.error || t('Failed to move batch step'))
    } finally {
      setMovingStep(false)
    }
  }
  const handleViewBatch = row => {
    if (!row?.batch_id) return

    setSelectedBatchId(row.batch_id)
    navigate(`/claims/batches/${row.batch_id}`)
  }

  const monthOptions = useMemo(() => {
    return Array.from({ length: 24 }, (_, index) => {
      const month = moment().subtract(index, 'months')
      return { value: month.format('YYYY-MM'), label: month.format('MMMM YYYY') }
    })
  }, [])
  const selectedMonthLabel = moment(createForm.start_date, 'YYYY-MM-DD').format('MMMM YYYY')
  const selectedDateRangeLabel = `${moment(createForm.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY')} - ${moment(createForm.end_date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`

  const columns = [
    {
      header: t('Actions'),
      body: row => (
        <div className='flex items-center gap-1.5'>
          <EyeIcon
            title={t('View Batch Details')}
            onClick={() => handleViewBatch(row)}
          />
          <MoveStepIcon
            title={t('Move Step')}
            disabled={(batchStepOrder[row?.current_step] || 0) <= 1 || movingStep}
            onClick={() => !movingStep && openMoveStepModal(row)}
          />
          <JobProgressIcon
            title={t('Current Job Progress')}
            disabled={!row?.current_job?.job_id}
            onClick={() => openCurrentJobModal(row)}
          />
        </div>
      )
    },
    {
      header: t('Batch No'),
      body: row => (
        <div>
          <div>{row?.batch_number || '-'}</div>
          <div className='text-xs text-gray-500 break-words'>
            {row?.tpa_name || t('TPA not configured')} - {row?.company_count ?? row?.payers?.length ?? 0} {t('companies')}
          </div>
        </div>
      ),
      bodyCellClass: '!whitespace-normal !min-w-[220px]'
    },
    {
      header: t('Generated Amount'),
      body: row => {
        const financials = getBatchFinancials(row)
        return formatBatchAmount(Number(financials.company_share || 0) + Number(financials.patient_share || 0))
      }
    },
    { header: t('Company Share'), body: row => formatBatchAmount(getBatchFinancials(row).company_share) },
    { header: t('Patient Share'), body: row => formatBatchAmount(getBatchFinancials(row).patient_share) },
    { header: t('Discount'), body: row => formatBatchAmount(getBatchFinancials(row).discount) },
    { header: t('VAT'), body: row => formatBatchAmount(getBatchFinancials(row).vat) },
    {
      header: t('Visits'),
      body: row => row?.visit_count || 0
    },
    {
      header: t('Current Step'),
      body: row => normalizeText(row?.current_step) || '-'
    },
    {
      header: t('Status'),
      body: row => row?.current_status || '-'
    }
  ]

  return (
    <div>
      <div className='flex flex-wrap gap-2 mb-2'>
        <div className='w-full sm:w-[250px]'>
          <DropdownField
            useFormik={false}
            options={monthOptions}
            value={form.month}
            handleChange={event => setForm(prev => ({ ...prev, month: event.target?.value || '' }))}
            placeholder={t('Select Month')}
          />
        </div>

        <div className='w-[230px]'>
          <TextInput
            useFormik={false}
            value={form.search}
            handleChange={event => setForm(prev => ({ ...prev, search: event.target.value }))}
            placeholder={t('Search Batch No')}
          />
        </div>

        <div className='w-full lg:w-[420px]'>
          <MultiSelectField
            useFormik={false}
            value={toEffectiveValue(form.payer_ids, groupedPayerOptions)}
            handleChange={handlePayerChange}
            options={groupedPayerOptions}
            optionLabel='label'
            optionValue='value'
            optionGroupLabel='label'
            optionGroupChildren='options'
            placeholder={t('Select Payer')}
            filter
            display='chip'
            filterPlaceholder={t('Search')}
            selectedItemTemplate={option => tokenChipTemplate(option, { t })}
          />
        </div>

        <div className='flex gap-2 items-start flex-wrap'>
          <CustomButton
            title={t('ApplyFilters')}
            buttonType='primary'
            icon='pi pi-filter'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={handleApplyFilters}
          />
          <CustomButton
            title={t('Reset')}
            buttonType='reset'
            icon='pi pi-sync'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={handleResetFilters}
          />
          <CustomButton
            label={t('Generate Batches')}
            buttonType='success'
            icon={<MdOutlineAdd size={16} />}
            className='w-fit h-[35px]'
            onClick={() => setIsCreateModalVisible(true)}
          />
        </div>
      </div>

      {!!selectedPayerNames.length && (
        <div className='mb-3 text-sm text-gray-500'>
          {t('Selected')}: {selectedPayerNames.join(', ')}
        </div>
      )}

      <ReusableModal
        isVisible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
        title={t('Generate Batches')}
        HeaderIcon={MdOutlineCalendarMonth}
        position='top'
        className='w-[95vw] md:w-[720px]'
      >
        <div className='space-y-3'>
          <div className='flex flex-wrap gap-2'>
            <CustomButton label={`${t('Month')}: ${selectedMonthLabel}`} icon={<MdOutlineCalendarMonth size={14} />} buttonType={createMode === 'month' ? 'primary' : 'reset'} onClick={() => setCreateMode('month')} />
            <CustomButton label={`${t('Date Range')}: ${selectedDateRangeLabel}`} icon={<MdOutlineDateRange size={14} />} buttonType={createMode === 'range' ? 'primary' : 'reset'} onClick={() => setCreateMode('range')} />
          </div>

          {createMode === 'month' ? (
            <div className='w-full md:w-[280px]'>
              <input
                type='month'
                value={moment(createForm.start_date, 'YYYY-MM-DD').format('YYYY-MM')}
                onChange={handleMonthChange}
                className='input-field h-[36px] w-full rounded-md border border-gray-300 px-3 text-sm'
              />
            </div>
          ) : (
            <div className='flex flex-wrap gap-2'>
              <div className='w-full md:w-[260px]'>
                <DatePicker name='create_start_date' value={createForm.start_date ? moment(createForm.start_date, 'YYYY-MM-DD').toDate() : ''} handleChange={event => setCreateForm(prev => ({ ...prev, start_date: moment(event.value).format('YYYY-MM-DD') }))} useFormik={false} placeholder={t('StartDate')} />
              </div>
              <div className='w-full md:w-[260px]'>
                <DatePicker name='create_end_date' value={createForm.end_date ? moment(createForm.end_date, 'YYYY-MM-DD').toDate() : ''} handleChange={event => setCreateForm(prev => ({ ...prev, end_date: moment(event.value).format('YYYY-MM-DD') }))} useFormik={false} placeholder={t('EndDate')} />
              </div>
            </div>
          )}

          <div className='rounded border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700'>
            {t('All eligible insurance visits in the selected period will be grouped automatically by TPA. Payers without a configured TPA will be generated as separate batches.')}
          </div>

          <div className='flex justify-end gap-2 pt-2'>
            <CustomButton label={t('Cancel')} buttonType='reset' onClick={() => setIsCreateModalVisible(false)} disabled={creating} />
            <CustomButton label={t('Generate Batches')} buttonType='success' onClick={() => setShowGenerateConfirm(true)} loading={creating} disabled={creating} />
          </div>
        </div>
      </ReusableModal>
      <ConfirmationModal
        visible={showGenerateConfirm}
        setVisible={setShowGenerateConfirm}
        header={t('Generate Batches')}
        message={
          createMode === 'month'
            ? t('Are you sure you want to generate batches for {{month}}? All eligible insurance visits will be grouped automatically.', { month: selectedMonthLabel })
            : t('Are you sure you want to generate batches for {{dateRange}}? All eligible insurance visits will be grouped automatically.', { dateRange: selectedDateRangeLabel })
        }
        Icon={MdOutlineCalendarMonth}
        acceptLabel={t('Generate Batches')}
        rejectLabel={t('Cancel')}
        isLoading={creating}
        position='top'
        onAccept={async () => {
          await handleCreate()
        }}
        onReject={() => setShowGenerateConfirm(false)}
      />
      <RemarkModal
        isVisible={!!moveBatch}
        onClose={() => setMoveBatch(null)}
        title={t('Move Batch Step')}
        HeaderIcon={MdOutlineMoveUp}
        confirmLabel={t('Move Step')}
        buttonType='success'
        onConfirm={remarks => handleMoveStep(remarks)}
        loading={movingStep}
        contextText={`${t('Batch No')}: ${moveBatch?.batch_number || '-'}`}
      >
        <div>
          <label className='input-label'>{t('Move To Step')}</label>
          <DropdownField
            useFormik={false}
            value={moveForm.target_step}
            handleChange={event =>
              setMoveForm(prev => ({
                ...prev,
                target_step: event.target.value || ''
              }))
            }
            options={moveStepOptions}
            placeholder={t('Select Step')}
          />
        </div>
      </RemarkModal>
      <JobProgressModal
        isVisible={!!jobBatch}
        onClose={() => setJobBatch(null)}
        batchId={jobBatch?.batch_id}
        batchName={jobBatch?.batch_number || ''}
      />
      <CustomTable
        data={batches}
        columns={columns}
        isLoading={loading}
        emptyMessage={t('No batches found')}
      />

      {pagination.total_count > 0 && (
        <CustomPagination
          currentPage={pagination.page}
          rowsPerPage={pagination.per_page}
          totalRecords={pagination.total_count}
          totalPages={pagination.total_pages}
          onPageChange={(page, perPage) =>
            fetchBatches(page, perPage, form)
          }
        />
      )}

    </div>
  )
}

export default ClaimBatchListTab
